import { ChevronRight } from "lucide-react";
import { cn } from "../lib/cn";
import { panelCard } from "./surface";

/**
 * One thing on a panel: a quiet label above whatever it currently says.
 * Pressing a tile hands it back, so the panel can grow its next pane out of
 * the tile that was pressed. A tile with nothing behind it stays in place.
 */
export function PanelTile({
  label,
  className,
  onOpen,
  children,
}: {
  label: string;
  className?: string;
  onOpen?: (tile: HTMLElement) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={!onOpen}
      onClick={event => onOpen?.(event.currentTarget)}
      className={cn(
        panelCard,
        "group flex min-w-0 flex-col items-start gap-1.5 p-3 text-left transition duration-200 hover:bg-foreground/10 active:scale-[0.98] disabled:pointer-events-none",
        className,
      )}
    >
      <span className="flex w-full items-center gap-0.5 text-xs text-muted-foreground">
        {label}
        <ChevronRight
          aria-hidden
          className="size-3 -translate-x-1 opacity-0 transition duration-200 group-hover:translate-x-0 group-hover:opacity-100"
        />
      </span>
      <span className="flex w-full min-w-0 flex-col items-start gap-0.5">{children}</span>
    </button>
  );
}

/**
 * Where `tile` sits inside `pane`, as a transform-origin. A pane that grows
 * from this point reads as the tile that was clicked opening in place.
 */
export function growOrigin(pane: HTMLElement | null, tile: HTMLElement): string {
  if (!pane) return "center";
  const bounds = tile.getBoundingClientRect();
  const within = pane.getBoundingClientRect();
  const x = bounds.left + bounds.width / 2 - within.left;
  const y = bounds.top + bounds.height / 2 - within.top;
  return `${Math.round(x)}px ${Math.round(y)}px`;
}
